/*
 * PIKL LA — inline hover quick-add (<product-card-quick-add>).
 *
 * A mini variant picker shown over the card meta on hover (desktop) or tap
 * (mobile): selecting options snaps to an available variant and updates the
 * reused <product-form>'s id input; the Add-to-Cart submit button adds it and
 * opens the cart drawer via the theme's own path. No bare imports.
 *
 * Reveal is gated on the `pikl-qa--ready` class added here, so a failed JS load
 * never shows a non-functional picker (the card image/title link → PDP remains).
 */
class ProductCardQuickAdd extends HTMLElement {
  #onPickerClick;
  #onOpenClick;
  #onEnter;
  #onLeave;
  #lastColor;

  connectedCallback() {
    this.matrix = this.#parseMatrix();
    if (!this.matrix) return;

    this.card = this.closest("product-card");
    this.form = this.querySelector("form");
    this.idInput = this.form ? this.form.querySelector('input[name="id"]') : null;
    this.atc = this.querySelector(".pikl-qa__atc");
    this.picker = this.querySelector(".pikl-qa__picker");
    this.openBtn = this.querySelector(".pikl-qa__open");
    this.hoverImg = this.card ? this.card.querySelector(".product-card__img-hover") : null;
    if (!this.form || !this.idInput || !this.atc || !this.picker) return;

    this.buttons = Array.from(
      this.picker.querySelectorAll("[data-option-position][data-option-value]")
    );
    this.positions = [
      ...new Set(this.buttons.map((b) => Number(b.dataset.optionPosition))),
    ].sort((a, b) => a - b);
    if (this.positions.length === 0) return;

    // Seed from the variant whose image the card loads with (the featured-media
    // variant) so the pre-selected color matches the photo — but only if it's in
    // stock, otherwise fall back to the first available variant so ATC is armed.
    const seedId = this.matrix.selectedId;
    const seed =
      this.matrix.variants.find((v) => v.id === seedId && v.a) ||
      this.matrix.variants.find((v) => v.a) ||
      this.matrix.variants[0];
    this.selected = {};
    this.positions.forEach((pos) => {
      this.selected[pos] = seed.o[pos - 1];
    });

    this.#onPickerClick = this.#handlePickerClick.bind(this);
    this.picker.addEventListener("click", this.#onPickerClick);
    if (this.openBtn) {
      this.#onOpenClick = this.#handleOpenClick.bind(this);
      this.openBtn.addEventListener("click", this.#onOpenClick);
    }

    this.#refresh();
    const seeded = this.picker.querySelector(".pikl-qa__swatch.is-selected");
    this.#lastColor = seeded ? seeded.dataset.optionValue : null;
    this.#setHoverImage(this.#lastColor);
    if (this.card) this.card.classList.add("pikl-qa--ready");

    // Drive the desktop reveal from JS (mouseenter) rather than CSS :hover — some
    // browsers/displays mis-match the (hover)/min-width media queries even on
    // desktop, leaving a CSS-only reveal stuck hidden. Touch uses the open button.
    if (this.card && document.documentElement.classList.contains("no-touchevents")) {
      this.#onEnter = () => this.card.classList.add("pikl-qa--hover");
      this.#onLeave = () => this.card.classList.remove("pikl-qa--hover");
      this.card.addEventListener("mouseenter", this.#onEnter);
      this.card.addEventListener("mouseleave", this.#onLeave);
    }
  }

  disconnectedCallback() {
    if (this.picker && this.#onPickerClick) {
      this.picker.removeEventListener("click", this.#onPickerClick);
    }
    if (this.openBtn && this.#onOpenClick) {
      this.openBtn.removeEventListener("click", this.#onOpenClick);
    }
    if (this.card && this.#onEnter) {
      this.card.removeEventListener("mouseenter", this.#onEnter);
      this.card.removeEventListener("mouseleave", this.#onLeave);
    }
  }

  #parseMatrix() {
    const el = this.querySelector(".js-pikl-variants");
    if (!el) return null;
    try {
      const data = JSON.parse(el.textContent);
      if (!data || !Array.isArray(data.variants) || data.variants.length === 0) {
        return null;
      }
      return data;
    } catch (error) {
      return null;
    }
  }

  #matches(variant) {
    return this.positions.every((pos) => variant.o[pos - 1] === this.selected[pos]);
  }

  #resolve() {
    return this.matrix.variants.find((v) => this.#matches(v));
  }

  #handlePickerClick(e) {
    const btn = e.target.closest("[data-option-position][data-option-value]");
    if (!btn || btn.disabled || !this.picker.contains(btn)) return;
    e.preventDefault();

    const pos = Number(btn.dataset.optionPosition);
    const value = btn.dataset.optionValue;
    this.selected[pos] = value;

    // Snap the other axes to a valid (preferably available) variant for this value.
    const variants = this.matrix.variants;
    const candidate =
      variants.find((v) => v.a && this.#matches(v)) ||
      variants.find((v) => v.a && v.o[pos - 1] === value) ||
      variants.find((v) => v.o[pos - 1] === value);
    if (candidate) {
      this.positions.forEach((p) => {
        this.selected[p] = candidate.o[p - 1];
      });
    }

    this.#refresh();
    this.#syncImage();
  }

  // When the selected color changes, swap the card image by triggering the
  // theme's own (hidden) swatch → reuses product-cards.js updateImage + srcset.
  #syncImage() {
    if (!this.card) return;
    const sel = this.picker.querySelector(".pikl-qa__swatch.is-selected");
    if (!sel) return;
    const color = sel.dataset.optionValue;
    if (color === this.#lastColor) return;
    this.#lastColor = color;
    this.#setHoverImage(color);
    const swatch = Array.from(
      this.card.querySelectorAll("product-card-variant-swatch[data-pikl-option-value]")
    ).find((s) => s.dataset.piklOptionValue === color);
    if (swatch) swatch.click();
  }

  // Point the card's hover (second) image at the selected color's 2nd photo,
  // grouped by alt-text in the matrix. No-op until the merchant groups media.
  #setHoverImage(color) {
    if (!this.hoverImg || !color || !this.matrix.colorHover) return;
    const url = this.matrix.colorHover[color];
    if (!url) return;
    this.hoverImg.setAttribute("srcset", "");
    this.hoverImg.src = url;
  }

  #handleOpenClick() {
    if (!this.card) return;
    const open = this.card.classList.toggle("pikl-qa--open");
    this.openBtn.setAttribute("aria-expanded", open ? "true" : "false");
  }

  // Sync active states, grey unavailable values, and arm/disarm Add-to-Cart.
  #refresh() {
    this.buttons.forEach((btn) => {
      const pos = Number(btn.dataset.optionPosition);
      const value = btn.dataset.optionValue;
      btn.classList.toggle("is-selected", this.selected[pos] === value);
      const available = this.matrix.variants.some(
        (v) =>
          v.a &&
          v.o[pos - 1] === value &&
          this.positions.every((p) => p === pos || v.o[p - 1] === this.selected[p])
      );
      btn.disabled = !available;
      btn.classList.toggle("pikl-qa__soldout", !available);
      btn.toggleAttribute("aria-disabled", !available);
    });

    const variant = this.#resolve();
    if (variant && variant.a) {
      this.idInput.value = variant.id;
      this.atc.disabled = false;
      this.atc.removeAttribute("aria-disabled");
    } else {
      this.idInput.value = "";
      this.atc.disabled = true;
      this.atc.setAttribute("aria-disabled", "true");
    }
  }
}

if (!customElements.get("product-card-quick-add")) {
  customElements.define("product-card-quick-add", ProductCardQuickAdd);
}
